<template>
  <onboarding-form-layout
    :title="$t('incremental_onboarding.essentials.title')"
    :description="$t('incremental_onboarding.essentials.description')"
    :loading="submitting"
    :prefilled="isPrefilled"
    @proceed="proceed"
  >
    <!-- Input Fields -->
    <div class="s-flex s-gap-4">
      <InputField
        id="company-name"
        class-name="s-flex-1"
        data-testid="company-name"
        :show-mandatory="true"
        :label="$t('incremental_onboarding.essentials.fields.legalBusinessName.label')"
        :value="getField(EssentialFormField.COMPANY_NAME)"
        :set-value="handleInputChange(EssentialFormField.COMPANY_NAME)"
        :error="getErrors[EssentialFormField.COMPANY_NAME]"
        :hint="$t('businessProfile.companyName.instruction')"
      />

      <InputField
        id="doing-business-as"
        class-name="s-flex-1"
        data-testid="doing-business-as"
        :label="$t('incremental_onboarding.essentials.fields.doingBusinessAs.label')"
        :value="getField(EssentialFormField.DOING_BUSINESS_AS)"
        :set-value="handleInputChange(EssentialFormField.DOING_BUSINESS_AS)"
        :error="getErrors[EssentialFormField.DOING_BUSINESS_AS]"
        :hint="$t('businessProfile.doingBusinessAs.instruction')"
      />
    </div>

    <!-- Type of Business -->
    <div class="select-field s-flex s-flex-col s-items-start" data-testid="type-of-business">
      <span>
        <s-label
          :label="$t('incremental_onboarding.essentials.fields.typeOfBusiness.label')"
          class="s-font-medium"
        />
        <span class="s-text-danger-600">*</span>
      </span>

      <s-select
        autofocus
        :searchable="false"
        :clearable="true"
        id="type-of-business-select"
        :options="getTypesOfBusiness()"
        @update="checkAndSetTypeOfBusiness"
        :value="getField(EssentialFormField.TYPE_OF_BUSINESS)"
        width-menu="full"
        :is-invalid="Boolean(getErrors[EssentialFormField.TYPE_OF_BUSINESS])"
        :invalid-message="getErrors[EssentialFormField.TYPE_OF_BUSINESS]"
        class="type-of-business-select"
      >
        <template #option="{ option }">
          <div class="type-of-business-option s-flex s-flex-col s-text-wrap s-text-left s-p-1">
            <div class="s-font-bold s-mb-1">
              {{ option.displayText }}
            </div>
            <div class="s-text-neutral-400 s-leading-snug">
              {{ option.desc }}
            </div>
          </div>
        </template>
      </s-select>
    </div>

    <!-- Ownership Type -->
    <div class="s-flex s-flex-col s-items-start" data-testid="ownership-type">
      <span>
        <s-label
          :label="$t('incremental_onboarding.essentials.fields.ownershipType.label')"
          class="s-font-medium"
        />
        <span class="s-text-danger-600">*</span>
      </span>

      <div class="radio-group s-mt-1 s-flex s-gap-6">
        <s-label v-for="type in ownershipTypes" :key="type.value">
          <input
            type="radio"
            name="ownership"
            :value="type.value"
            :checked="getField(EssentialFormField.OWNERSHIP_TYPE) === type.value"
            :disabled="disableOwnershipTypeSelect"
            @change="() => updateFieldFromValue({ key: EssentialFormField.OWNERSHIP_TYPE, value: type.value })"
          />
          {{ $t(type.labelKey) }}
        </s-label>
      </div>

      <c-inline-error
        v-if="getErrors[EssentialFormField.OWNERSHIP_TYPE]"
        :message="getErrors[EssentialFormField.OWNERSHIP_TYPE]"
      />
    </div>

    <!-- Business Country -->
    <SelectField
      :label="$t('incremental_onboarding.essentials.fields.businessLocation.label')"
      :hint="$t('incremental_onboarding.essentials.fields.businessLocation.instruction')"
      :searchable="true"
      :clearable="true"
      :show-mandatory="true"
      :value="getField(EssentialFormField.BUSINESS_COUNTRY)"
      :options="getCountriesEnabledForCbPayments()"
      :on-update="changeCountry"
      :error="getErrors[EssentialFormField.BUSINESS_COUNTRY]"
      select-classname="country-select"
      field-classname="country-field"
      data-testid="country-field"
    />
  </onboarding-form-layout>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import TypesOfBusinessList from '@/data/type-of-business-list.ts';
import {BusinessType, OnboardingRequest, OwnershipType} from '@/api/paymentApi';
import {EssentialFormField} from '@/store/modules/incremental-onboarding/essentials';
import InputField from '@/components/common/InputField.vue';
import SelectField from '@/components/common/SelectField.vue';
import merchantOnboardingApi from '@/api/merchantOnboardingApi';
import {OnboardingFormMixin} from "@/components/incremental-onboarding/OnboardingFormMixin";
import OnboardingFormLayout from "@/components/incremental-onboarding/OnboardingFormLayout.vue";
import {CBPaymentsEnabledCountries} from "@/data/country-code-list";
import {BusinessAddressFields} from "@/store/modules/incremental-onboarding/incrementalOnboardingbusinessAddress";
import FullStoryHelper from '@/app/utils/fullstory';
import { ESSENTIALS_FORM_PROCEED } from '@/app/utils/fullstory/constants';

export default {
  name: 'BusinessStructure',
  components: {InputField, SelectField, OnboardingFormLayout},
  mixins: [OnboardingFormMixin],
  computed: {
    ...mapGetters('essentials', ['getField', 'getErrors', 'getIncrementalOnboardingData', 'isDirty', 'isPrefilled']),
    ...mapGetters('stakeholdersDetails', ['getFirstName']),
    OwnershipType: () => OwnershipType,
    EssentialFormField: () => EssentialFormField,
    disableOwnershipTypeSelect() {
      const type = this.getField(EssentialFormField.TYPE_OF_BUSINESS);
      return [BusinessType.INDIVIDUALSOLEPROPRIETOR, BusinessType.GOVERNMENTENTITY, BusinessType.TAXEXEMPT].includes(type);
    },
    ownershipTypes() {
      return [
        {
          value: OwnershipType.PRIVATE,
          labelKey: 'incremental_onboarding.essentials.fields.ownershipType.values.private',
        },
        {
          value: OwnershipType.PUBLIC,
          labelKey: 'incremental_onboarding.essentials.fields.ownershipType.values.public',
        },
      ];
    },
  },
  methods: {
    ...mapActions('essentials', [
      'updateFieldFromEvent', 'updateFieldFromValue',
      'validate', 'resetDirty', 'setErrors']),
    ...mapActions('incrementalOnboardingBusinessAddress', {
      updateCountryInIncrementalBusinessAddressStore: 'updateFieldFromValue'
    }),
    ...mapActions('businessAddress', {updateCountryInBusinessAddressStore: 'updateCountry'}),
    ...mapActions('app', ['updateAppState']),
    ...mapActions('stakeholdersDetails', ['validateControlPersonCountry', 'setAddressCountry']),
    async proceed() {
      const apiCall = async () => {
        return this.getMerchantId
            ? await this.updateMerchantBusinessType()
            : await this.initiateMerchantOnboarding();
      };

      // Emit FullStory event with update prefix if merchant already exists
      FullStoryHelper.event(ESSENTIALS_FORM_PROCEED,);

      await this.submitForm(apiCall);
    },

    async initiateMerchantOnboarding() {
      const response = await merchantOnboardingApi.initiateMultiStepMerchantOnboarding({
        ...this.getIncrementalOnboardingData,
        requestType: OnboardingRequest.RequestTypeEnum.INITMERCHANTONBOARDING,
      });
      await this.updateAppState({key: 'merchantId', value: response.id});
      return response;
    },

    async updateMerchantBusinessType() {
      return await merchantOnboardingApi.updateBusinessType(this.getMerchantId, {
        ...this.getIncrementalOnboardingData,
        requestType: OnboardingRequest.RequestTypeEnum.UPDATEBUSINESSTYPE,
      });
    },

    changeCountry(countryCode) {
      this.updateCountryInBusinessAddressStore(countryCode);
      this.updateCountryInIncrementalBusinessAddressStore({key: BusinessAddressFields.COUNTRY, value: countryCode})
      this.updateFieldFromValue({key: EssentialFormField.BUSINESS_COUNTRY, value: countryCode});
      if(!this.getFirstName(0)?.value) {
        //Setting control person country to the selected country until control person is added
        this.setAddressCountry({ 'i': 0, 'value': countryCode });
      }
      this.validateControlPersonCountry()
    },

    getCountriesEnabledForCbPayments() {
      //filter out countries with restricted env
      return CBPaymentsEnabledCountries.filter(x => !(x.restrictedEnv && x.restrictedEnv.includes(process.env.VUE_APP_BUILD_ENV)))
    },
    getTypesOfBusiness: () => TypesOfBusinessList,

    checkAndSetTypeOfBusiness(businessType) {
      let ownershipValue;
      if (businessType === BusinessType.INDIVIDUALSOLEPROPRIETOR) {
        ownershipValue = OwnershipType.PRIVATE;
      } else {
        ownershipValue = [BusinessType.GOVERNMENTENTITY, BusinessType.TAXEXEMPT].includes(businessType)
            ? OwnershipType.PUBLIC : null;
      }

      if (ownershipValue) {
        this.updateFieldFromValue({key: EssentialFormField.OWNERSHIP_TYPE, value: ownershipValue});
      }

      this.updateFieldFromValue({key: EssentialFormField.TYPE_OF_BUSINESS, value: businessType});
    },
  },
};
</script>
